import { Controller } from "@hotwired/stimulus"
import { formatBRL } from "../utils/format"

// The POS cart: gates items behind a loaded card, keeps the running total within
// the card balance, enforces a 10s per-card cooldown (mirrors the server guard),
// and disables confirm while submitting. Ported from APM.
export default class extends Controller {
  static targets = ["total", "confirmBtn", "quantityInput", "balanceWarning", "itemsSection"]
  static values  = { balance: Number }

  connect() {
    this._boundOnCardLoaded   = this.#onCardLoaded.bind(this)
    this._boundSubmitStart    = this.#onSubmitStart.bind(this)
    this._boundSubmitEnd      = this.#onSubmitEnd.bind(this)
    this._boundMenuRefreshed   = this.#recalculate.bind(this)
    this.element.addEventListener("card:loaded",        this._boundOnCardLoaded)
    this.element.addEventListener("turbo:submit-start", this._boundSubmitStart)
    this.element.addEventListener("turbo:submit-end",   this._boundSubmitEnd)
    // Dispatched by kiosk-stock-channel after it applies a live menu update — a
    // clamped quantity, a now-maxed-out item or a changed unit price all move
    // the total/button state.
    this.element.addEventListener("menu:refreshed",      this._boundMenuRefreshed)
    this._currentCardId = null
    this._pendingCardId = null
    this.#setItemsEnabled(false)
    this.#recalculate()
  }

  disconnect() {
    clearTimeout(this._cooldownTimer)
    clearTimeout(this._warningTimer)
    this.element.removeEventListener("card:loaded",        this._boundOnCardLoaded)
    this.element.removeEventListener("turbo:submit-start", this._boundSubmitStart)
    this.element.removeEventListener("turbo:submit-end",   this._boundSubmitEnd)
    this.element.removeEventListener("menu:refreshed",     this._boundMenuRefreshed)
  }

  increment(event) {
    if (this._currentCardId === null) return
    const input     = this.#inputFor(event)
    const unitPrice = parseFloat(input.dataset.unitPrice || "0")
    const maxQtyRaw = input.dataset.maxQty
    const maxQty    = maxQtyRaw !== "" ? parseInt(maxQtyRaw, 10) : Infinity
    const current   = parseInt(input.value || "0", 10)

    if (current >= maxQty) return

    if (this.#currentTotal() + unitPrice > this.balanceValue) {
      this.#showBalanceWarning()
      return
    }
    input.value = current + 1
    this.#recalculate()
  }

  decrement(event) {
    if (this._currentCardId === null) return
    const input = this.#inputFor(event)
    const current = parseInt(input.value || "0", 10)
    if (current > 0) input.value = current - 1
    this.#recalculate()
  }

  // private

  #onCardLoaded({ detail: { id, balance } }) {
    this._currentCardId = id
    this.balanceValue   = parseFloat(balance)
    this.#setItemsEnabled(true)
    this.#recalculate()
  }

  #onSubmitStart() {
    if (!this.hasConfirmBtnTarget) return
    this._pendingCardId               = this._currentCardId
    this.confirmBtnTarget.disabled    = true
    this.confirmBtnTarget.textContent = "Processando..."
  }

  #onSubmitEnd({ detail: { success } }) {
    if (success && this._pendingCardId) {
      sessionStorage.setItem(`last_sale_card_${this._pendingCardId}`, Date.now())
    }
    this._pendingCardId = null
    if (!success) this.#recalculate()
  }

  #inputFor(event) {
    return event.currentTarget.closest("[data-item]").querySelector("[data-kiosk-pos-target='quantityInput']")
  }

  #cooldownRemaining(cardId) {
    const last = parseInt(sessionStorage.getItem(`last_sale_card_${cardId}`) || "0", 10)
    return Math.max(0, 10000 - (Date.now() - last))
  }

  #currentTotal() {
    return this.quantityInputTargets.reduce((sum, input) => {
      return sum + parseInt(input.value || "0", 10) * parseFloat(input.dataset.unitPrice || "0")
    }, 0)
  }

  #showBalanceWarning() {
    if (!this.hasBalanceWarningTarget) return
    this.balanceWarningTarget.hidden = false
    clearTimeout(this._warningTimer)
    this._warningTimer = setTimeout(() => {
      if (this.#currentTotal() <= this.balanceValue) this.balanceWarningTarget.hidden = true
    }, 2000)
  }

  #setItemsEnabled(enabled) {
    this.itemsSectionTargets.forEach(el => {
      el.classList.toggle("pointer-events-none", !enabled)
      el.classList.toggle("opacity-40",          !enabled)
    })
  }

  // Runs after every quantity change (increment, decrement, a card loading,
  // a failed submit, or a live stock refresh) so the corner badge and the X
  // never drift from the input they mirror.
  #syncTileVisibility(input) {
    const hasQty     = parseInt(input.value || "0", 10) > 0
    input.hidden     = !hasQty
    const removeBtn  = input.closest("[data-item]")?.querySelector('[data-role="remove-btn"]')
    if (removeBtn) removeBtn.hidden = !hasQty
  }

  #recalculate() {
    clearTimeout(this._cooldownTimer)
    this.quantityInputTargets.forEach((input) => this.#syncTileVisibility(input))
    const total = this.#currentTotal()

    if (this.hasTotalTarget) this.totalTarget.textContent = formatBRL(total)

    const hasBalance = this.balanceValue >= total
    const hasItems   = total > 0
    const cooldown   = this._currentCardId ? this.#cooldownRemaining(this._currentCardId) : 0
    const inCooldown = cooldown > 0

    if (this.hasConfirmBtnTarget) {
      this.confirmBtnTarget.disabled    = !(hasItems && hasBalance && !inCooldown)
      this.confirmBtnTarget.textContent = inCooldown ? `Aguarde ${Math.ceil(cooldown / 1000)}s…` : "Confirmar venda"
    }

    if (this.hasBalanceWarningTarget) this.balanceWarningTarget.hidden = hasBalance || !hasItems

    if (inCooldown) this._cooldownTimer = setTimeout(() => this.#recalculate(), 1000)
  }
}
